import * as d3 from 'd3'

export default function bullet() {
  var orient = 'left',
    reverse = false,
    duration = 0,
    ranges = bulletRanges,
    markers = bulletMarkers,
    measures = bulletMeasures,
    width = 380,
    height = 59,
    tickFormat = function (d) {
      // Use D3's SI format with one significant digit.
      var f = d3.format('.1s')(d)
      // Replace lower-case suffixes with upper-case ones:
      return f.replace('k', 'K').replace('m', 'M')
    }

  function bullet(g) {
    g.each(function (d, i) {
      var order = d.order
      var prefix = d.prefix || ''

      // Get our data arrays and sort them appropriately
      var rangez = ranges.call(this, d, i).slice(),
        markerz = markers.call(this, d, i).slice().sort(d3.descending),
        measurez = measures.call(this, d, i).slice().sort(d3.descending)

      // For a "high-is-good" bullet chart, we want the worst (lowest) range first
      // and the best (highest) last. For "low-is-good" it is the opposite.
      if (order === 'high-is-good') {
        rangez.sort(d3.descending)
      } else {
        rangez.sort(d3.ascending)
      }

      // Scale setup
      var allValues = rangez.concat(markerz, measurez)
      var minData = d3.min(allValues, (d) => +d)
      var maxData = d3.max(allValues, (d) => +d)
      if (minData > 0) minData = 0

      var x1 = d3
        .scaleLinear()
        .domain([minData, maxData])
        .range(reverse ? [width, 0] : [0, width])

      function bulletWidth(x) {
        var base = x(0)
        return function (d) {
          return Math.abs(x(d) - base)
        }
      }

      function bulletX(x) {
        var base = x(0)
        return function (d) {
          return Math.min(base, x(d))
        }
      }

      var w1 = bulletWidth(x1)
      var x1pos = bulletX(x1)
      var offset = minData < 0 ? -x1(minData) : -x1(0)

      var inner = d3.select(this).select('g.bulletChart')
      if (inner.empty()) {
        inner = d3.select(this).append('g').attr('class', 'bulletChart')
      }
      inner.attr('transform', 'translate(' + offset + ',0)')

      if (order === 'low-is-good') {
        rangez.sort(d3.descending)
      }

      // Update ranges
      var range = inner.selectAll('rect.range').data(rangez)
      range
        .enter()
        .append('rect')
        .attr('class', function (d, i) {
          return 'range s' + (order === 'high-is-good' ? i : rangez.length - 1 - i)
        })
        .merge(range)
        .attr('x', x1pos)
        .attr('width', w1)
        .attr('height', height)

      // Sort the range elements by x position first, then by width
      inner.selectAll('rect.range').sort(function (a, b) {
        var ax = x1pos(a)
        var bx = x1pos(b)
        if (ax !== bx) return ax - bx // Sort by x position first
        return w1(b) - w1(a) // If x positions are equal, sort by width (descending)
      })

      // Update measures
      var measure = inner.selectAll('rect.measure').data(measurez)
      measure
        .enter()
        .append('rect')
        .attr('class', (d, i) => 'measure s' + i)
        .merge(measure)
        .attr('width', w1)
        .attr('height', height / 3)
        .attr('x', x1pos)
        .attr('y', height / 3)

      // Update markers
      var marker = inner.selectAll('line.marker').data(markerz)
      marker
        .enter()
        .append('line')
        .attr('class', 'marker')
        .merge(marker)
        .attr('x1', (d) => x1(d))
        .attr('x2', (d) => x1(d))
        .attr('y1', height / 4)
        .attr('y2', (height * 3) / 4)

      // Update ticks based on data type
      var format = tickFormat || x1.tickFormat(8)
      var domain = x1.domain()
      var tickValues

      if (prefix === '$') {
        var max = domain[1]

        var step

        // Generate initial tick values
        tickValues = [0]

        // For values under 1M, always include 50K steps
        if (max < 1000000) {
          for (var v = 50000; v <= max; v += 50000) {
            tickValues.push(v)
          }
          // If we have values over 100K, add 100K steps
          if (max > 100000) {
            for (v = 100000; v <= max; v += 100000) {
              tickValues.push(v)
            }
          }
        } else {
          // For values over 1M, use million steps
          step = 1000000
          for (v = step; v <= max; v += step) {
            tickValues.push(v)
          }
        }

        // Add negative values if needed
        if (domain[0] < 0) {
          tickValues.unshift(-50000)
        }

        // Remove duplicates and sort
        tickValues = [...new Set(tickValues)].sort((a, b) => a - b)

        // Remove duplicates based on formatted labels
        const formattedSet = new Set()
        tickValues = tickValues.filter((v) => {
          const formatted = tickFormat(v)
          if (formattedSet.has(formatted)) return false // Skip duplicates
          formattedSet.add(formatted)
          return true
        })

        // Filter to domain range
        tickValues = tickValues.filter((v) => v >= domain[0] && v <= domain[1])
      } else {
        // For percentage and other fields
        tickValues = x1
          .ticks(8)
          .filter(
            (value, index, self) => self.findIndex((v) => format(v) === format(value)) === index
          )
      }

      var tick = inner.selectAll('g.tick').data(tickValues, function (d) {
        return format(d)
      })

      var tickEnter = tick
        .enter()
        .append('g')
        .attr('class', 'tick')
        .attr('transform', function (d) {
          return 'translate(' + x1(d) + ',0)'
        })
        .style('opacity', 1)

      tickEnter
        .append('line')
        .attr('y1', height)
        .attr('y2', height + height / 4)

      tickEnter
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '1em')
        .attr('y', height + height / 15)
        .text(format)

      // Update
      tick
        .attr('transform', function (d) {
          return 'translate(' + x1(d) + ', 0)'
        })
        .select('text')
        .text(format)

      // Exit
      tick.exit().remove()
    })
    d3.timerFlush()
  }

  // Fix the API methods
  bullet.orient = function (x) {
    if (!arguments.length) return orient
    orient = x
    return bullet
  }

  bullet.ranges = function (x) {
    if (!arguments.length) return ranges
    ranges = x
    return bullet
  }

  bullet.markers = function (x) {
    if (!arguments.length) return markers
    markers = x
    return bullet
  }

  bullet.measures = function (x) {
    if (!arguments.length) return measures
    measures = x
    return bullet
  }

  bullet.width = function (x) {
    if (!arguments.length) return width
    width = x
    return bullet
  }

  bullet.height = function (x) {
    if (!arguments.length) return height
    height = x
    return bullet
  }

  bullet.tickFormat = function (x) {
    if (!arguments.length) return tickFormat
    tickFormat = x
    return bullet
  }

  bullet.duration = function (x) {
    if (!arguments.length) return duration
    duration = x
    return bullet
  }

  return bullet
}

function bulletRanges(d) {
  return d.ranges
}
function bulletMarkers(d) {
  return d.markers
}
function bulletMeasures(d) {
  return d.measures
}
